import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./FichaProductor.scss";
import Badge from "../../../components/common/Badge/Badge";
import TabsNavigation from "../../../components/common/TabsNavigation/TabsNavigation";
import Table from "../../../components/common/Table/Table";
import DetalleLayout from "../../../Layouts/DetalleLayout/DetalleLayout";
import { useZone } from "../../../context/ZoneContext";
import { getApiInstance } from "../../../utils/api";
import { mapList } from "../../../utils/utils";

const DOCUMENTOS_SEARCH_TERMS = [
    "tipoDocumento",
    "comunidad",
    "provincia",
    "año",
    "documento",
];

const getDocumentosFilterFields = (t) => [
    {
        key: "codEstado",
        label: t("producerSheet.status"),
        type: "multiselect",
        labelMap: [
            "",
            t("producerSheet.pendingValidation"),
            t("producerSheet.validationWithIncidents"),
            t("producerSheet.validated"),
            t("producerSheet.expired2"),
        ],
    },
    {
        key: "tipoDocumento",
        label: t("producerSheet.documentType"),
        type: "multiselect",
    },
];

export default function FichaProductor({ user }) {
    const { t } = useTranslation();
    const { id } = useParams();
    const [productor, setProductor] = useState(null);
    const [documentos, setDocumentos] = useState([]);
    const [cumplimientoLegal, setCumplimientoLegal] = useState([]);
    const [activeTabGeneral, setActiveTabGeneral] = useState("informacion");
    const [activeTabDatos, setActiveTabDatos] = useState("documentacion");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [comunidades, setComunidades] = useState([]);
    const [paises, setPaises] = useState([]);
    const [provincias, setProvincias] = useState([]);

    const fetchProductor = async (id) => {
        try {
            const api = getApiInstance();
            console.log(`Fetching gestor with ID: ${id}`);
            const response = await api.get(
                `/api/Productores/GetProductorById/${encodeURIComponent(id)}`
            );
            console.log(`Response from fetchGestor:`, response.data);
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchDocumentos = async (id) => {
        try {
            const api = getApiInstance();
            console.log(`Fetching gestor with ID: ${id}`);
            const response = await api.get(
                `/api/ProductoresDocumento/GetDocumentosFromProductor/${encodeURIComponent(id)}`
            );
            console.log(`Response from fetchGestor:`, response.data);
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const handleDocumentosUpdate = useCallback(async () => {
        try {
            const data = await fetchDocumentos(id);
            setDocumentos(data);
        } catch (error) {
            console.error("Error refrescando documentos:", error);
        }
    }, [id]);

    const fetchCumplimientoLegal = async (id) => {
        try {
            const api = getApiInstance();
            console.log(`Fetching gestor with ID: ${id}`);
            const response = await api.post(
                `/api/CumplimientoLegal/GetCumplimientoByProductor/${encodeURIComponent(id)}`
            );
            console.log(`Response from fetchGestor:`, response.data);
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchComunidades = async () => {
        const api = getApiInstance();

        try {
            const response = await api.get(
                `/api/Comunidades/GetComunidades`
            );
            return response.data;
        } catch (err) {
            setError(
                t("producerSheet.couldNotLoadCommunitiesPleaseTryAgain")
            );
            console.error(err);
        } finally {
        }
    };

    const fetchPaises = async () => {
        const api = getApiInstance();

        try {
            const response = await api.get(
                `/api/Paises/GetPaises`
            );
            return response.data;
        } catch (err) {
            setError(
                t("producerSheet.couldNotLoadCountriesPleaseTryAgain")
            );
            console.error(err);
        } finally {
        }
    };

    const fetchProvincias = async () => {
        const api = getApiInstance();
        try {
            const response = await api.get(
                `/api/Provincias/GetProvincias`
            );
            return response.data;
        } catch (err) {
            setError(
                t("producerSheet.couldNotLoadProvincesPleaseTryAgain")
            );
            console.error(err);
        } finally {
        }
    };

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        const loadAll = async () => {
            try {
                const productor = await fetchProductor(id);

                if (isMounted) {
                    setProductor(productor);
                }

                const results = await Promise.allSettled([
                    fetchDocumentos(id),
                    fetchCumplimientoLegal(id),
                    fetchPaises(),
                    fetchComunidades(),
                    fetchProvincias(),
                ]);

                const [
                    documentosResult,
                    cumplimientoLegalResult,
                    paisesResult,
                    comunidadesResult,
                    provinciasResult,
                ] = results;

                if (isMounted) {
                    if (documentosResult.status === "fulfilled") {
                        setDocumentos(documentosResult.value);
                    } else {
                        console.warn(
                            "No se pudieron cargar los documentos:",
                            documentosResult.reason
                        );
                    }

                    if (cumplimientoLegalResult.status === "fulfilled") {
                        setCumplimientoLegal(cumplimientoLegalResult.value);
                    } else {
                        console.warn(
                            "No se pudieron cargar las eléctricas:",
                            cumplimientoLegalResult.reason
                        );
                    }

                    if (paisesResult.status === "fulfilled") {
                        setPaises(
                            mapList(
                                paisesResult.value,
                                "pais",
                                "codPais",
                                "codPais"
                            )
                        );
                    } else {
                        console.warn(
                            "No se pudieron cargar las eléctricas:",
                            paisesResult.reason
                        );
                    }

                    if (comunidadesResult.status === "fulfilled") {
                        setComunidades([
                            ...mapList(
                                comunidadesResult.value,
                                "comunidad",
                                "codComunidad",
                                "codComunidad",
                                "codPais"
                            ),
                        ]);
                    } else {
                        console.warn(
                            "No se pudieron cargar las eléctricas:",
                            comunidadesResult.reason
                        );
                    }

                    if (provinciasResult.status === "fulfilled") {
                        setProvincias([
                            ...mapList(
                                provinciasResult.value,
                                "provincia",
                                "codProvincia",
                                "codProvincia",
                                "codComunidad"
                            ),
                        ]);
                    } else {
                        console.warn(
                            "No se pudieron cargar las eléctricas:",
                            provinciasResult.reason
                        );
                    }
                }
            } catch (err) {
                if (isMounted) {
                    setError(
                        t("producerSheet.couldNotLoadTheMainDataPlease")
                    );
                }
                console.error("Error en datos críticos de loadAll:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadAll();

        return () => {
            isMounted = false;
        };
    }, [id]);

    const getEstadoBadge = (estado) => {
        const estadoConfig = {
            VALIDADO: { type: "success", label: t("producerSheet.validated") },
            PENDIENTE: { type: "info", label: t("producerSheet.pending") },
            INCIDENCIA: { type: "warning", label: t("producerSheet.incident") },
            VENCIDO: { type: "error", label: t("producerSheet.expired") },
            ACTIVA: { type: "success", label: t("producerSheet.active") },
        };
        return estadoConfig[estado] || estadoConfig["PENDIENTE"];
    };

    const tabsGeneral = [
        { id: "informacion", label: t("producerSheet.generalInformation") },
        { id: "ubicacion", label: t("producerSheet.location") },
    ];

    const tabsDatos = [
        { id: "documentacion", label: t("producerSheet.documentation") },
        { id: "cumplimiento", label: t("producerSheet.legalCompliance") },
    ];

    const renderInformacionGeneral = () => (
        <div className="ficha-gestor__info">
            <div className="ficha-gestor__field">
                <label>{t("producerSheet.nameColon")}</label>
                <span>{productor.nombre}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("producerSheet.taxIdColon")}</label>
                <span>{productor.cif}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("producerSheet.contactColon")}</label>
                <span>{productor.contacto}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("producerSheet.phoneColon")}</label>
                <span>{productor.telefono}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("producerSheet.emailColon")}</label>
                <span>{productor.eMail}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("producerSheet.representativeNameColon")}</label>
                <span>{productor.nombreRepresentante}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("producerSheet.representativeLastNameColon")}</label>
                <span>{productor.apellidosRepresentante}</span>
            </div>
        </div>
    );

    const renderLocalizacion = () => (
        <div className="ficha-gestor__info">
            <div className="ficha-gestor__field">
                <label>{t("producerSheet.addressColon")}</label>
                <span>{productor.direccion}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("producerSheet.localityColon")}</label>
                <span>{productor.localidad}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("producerSheet.provinceColon")}</label>
                <span>{productor.provincia}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("producerSheet.communityColon")}</label>
                <span>{productor.comunidad}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("producerSheet.countryColon")}</label>
                <span>{productor.pais}</span>
            </div>
        </div>
    );

    const renderGeneralContent = () => {
        switch (activeTabGeneral) {
            case "informacion":
                return renderInformacionGeneral();
            case "ubicacion":
                return renderLocalizacion();
            default:
                return renderInformacionGeneral();
        }
    };

    const renderDatosContent = () => {
        switch (activeTabDatos) {
            case "documentacion":
                return (
                    <div className="ficha-gestor__documentacion">
                        <Table
                            columns={[
                                {
                                    key: "tipoDocumento",
                                    label: t("producerSheet.documentType"),
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "comunidad",
                                    label: t("producerSheet.community"),
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "provincia",
                                    label: t("producerSheet.province"),
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "año",
                                    label: t("producerSheet.year"),
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "documento",
                                    label: t("producerSheet.document"),
                                    visible: true,
                                    sortable: true,
                                    documentProp: "ficheroBase64",
                                    type: "document",
                                },
                                {
                                    key: "codEstado",
                                    label: t("producerSheet.status"),
                                    visible: true,
                                    sortable: true,
                                },
                            ]}
                            dataTable={documentos}
                            onDataUpdate={handleDocumentosUpdate}
                            keyProp="codDocumento"
                            searchTerms={DOCUMENTOS_SEARCH_TERMS}
                            filterFields={getDocumentosFilterFields(t)}
                            noResultsMessage={t("producerSheet.noDocumentsAvailable")}
                            rowsPerPage={10}
                            showCheckboxes={false}
                            actions={["Eliminar"]}
                            elementName={t("elements.document")}
                            stateProp="codEstado"
                            states={[
                                { type: "primary", label: "" },
                                { type: "info", label: t("producerSheet.pendingValidation") },
                                {
                                    type: "warning",
                                    label: t("producerSheet.validationWithIncidents"),
                                },
                                { type: "success", label: t("producerSheet.validated") },
                                { type: "danger", label: t("producerSheet.expired2") },
                            ]}
                            formElements={[
                                {
                                    elements: [
                                        {
                                            name: "codTipoDocumento",
                                            type: "select",
                                            label: t("producerSheet.documentType"),
                                            placeholder:
                                                t("producerSheet.selectTheDocumentType"),
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                            options: [
                                                {
                                                    value: 2,
                                                    label: t("producerSheet.smallProducerRegistration"),
                                                },
                                                {
                                                    value: 3,
                                                    label: t("producerSheet.annualDeclaration"),
                                                },
                                            ],
                                        },
                                        {
                                            name: "año",
                                            type: "number",
                                            label: t("producerSheet.year"),
                                            placeholder: t("producerSheet.enterTheYear"),
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                            visibleWhen: {
                                                field: "codTipoDocumento",
                                                value: 3,
                                            },
                                        },
                                        {
                                            name: "codPais",
                                            type: "select",
                                            label: t("producerSheet.country"),
                                            placeholder: t("producerSheet.selectTheCountry"),
                                            options: paises,
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                        },
                                        {
                                            name: "codComunidad",
                                            type: "select",
                                            label: t("producerSheet.community"),
                                            placeholder:
                                                t("producerSheet.selectTheCommunity"),
                                            options: comunidades,
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                            associatedFormElement: "codPais",
                                        },
                                        {
                                            name: "codProvincia",
                                            type: "select",
                                            label: t("producerSheet.province"),
                                            placeholder:
                                                t("producerSheet.selectTheProvince"),
                                            options: provincias,
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                            associatedFormElement:
                                                "codComunidad",
                                        },
                                        {
                                            name: "codProductor",
                                            type: "number",
                                            label: "CodProductor",
                                            visible: false,
                                            defaultValue:
                                                productor?.codProductor ?? "",
                                        },
                                        {
                                            name: "codEstado",
                                            type: "number",
                                            label: "CodEstado",
                                            visible: false,
                                            defaultValue: 1,
                                        },
                                        {
                                            name: "fichero",
                                            type: "document",
                                            label: t("producerSheet.document"),
                                            placeholder: t("producerSheet.uploadDocument"),
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                        },
                                        {
                                            name: "obs",
                                            type: "textarea",
                                            label: t("producerSheet.observations"),
                                            placeholder:
                                                t("producerSheet.enterAdditionalObservations"),
                                            required: false,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                            rows: 3,
                                        },
                                    ],
                                },
                            ]}
                            addApiUrl="/api/ProductoresDocumento/SetDocumento"
                            deleteApiUrl="/api/ProductoresDocumento/DeleteDocumento"
                            loading={loading}
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                        />
                    </div>
                );
            case "cumplimiento":
                return (
                    <div className="ficha-gestor__tarifas">
                        <Table
                            columns={[
                                {
                                    key: "nombre",
                                    label: t("producerSheet.description"),
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "comunidad",
                                    label: t("producerSheet.community"),
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "año",
                                    label: t("producerSheet.year"),
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "codEstado",
                                    label: t("producerSheet.status"),
                                    visible: true,
                                    sortable: true,
                                },
                            ]}
                            dataTable={cumplimientoLegal}
                            noResultsMessage={t("producerSheet.noLegalComplianceAvailable")}
                            rowsPerPage={10}
                            showCheckboxes={false}
                            elementName={t("elements.legalCompliance")}
                            stateProp="codEstado"
                            states={[
                                { type: "primary", label: "" },
                                { type: "danger", label: t("producerSheet.notSubmitted") },
                                {
                                    type: "warning",
                                    label: t("producerSheet.validatedWithIncidents"),
                                },
                                { type: "success", label: t("producerSheet.validated") },
                                { type: "danger", label: t("producerSheet.expired2") },
                            ]}
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                            loading={loading}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <DetalleLayout
            title={productor ? t("producerSheet.titleN", { name: productor.nombre }) : t("common.loading")}
            backLink="/administracion/productores"
            backText={t("backTo.producers")}
            loading={loading}
            error={error}
            notFound={!productor && !loading && !error}
            className="ficha-gestor"
        >
            {!loading && !error && productor && (
                <div className="ficha-gestor__content">
                    <div className="ficha-gestor__section">
                        <div className="ficha-gestor__section-header">
                            <h2>{t("producerSheet.generalInformation")}</h2>
                        </div>

                        <TabsNavigation
                            tabs={tabsGeneral}
                            activeTab={activeTabGeneral}
                            onTabChange={setActiveTabGeneral}
                        />

                        <div className="ficha-gestor__section-content">
                            {renderGeneralContent()}
                        </div>
                    </div>

                    <div className="ficha-gestor__section">
                        <div className="ficha-gestor__section-header">
                            <h2>{t("producerSheet.producerData")}</h2>
                        </div>

                        <TabsNavigation
                            tabs={tabsDatos}
                            activeTab={activeTabDatos}
                            onTabChange={setActiveTabDatos}
                        />

                        <div className="ficha-gestor__section-content">
                            {renderDatosContent()}
                        </div>
                    </div>
                </div>
            )}
        </DetalleLayout>
    );
}
